<template>
  <Card class="PassengersCard" :title="t('ui.components.passengersCard.title', 2)" :description="t('ui.components.passengersCard.description')">
    <ActionButton
      class="PassengersCard-addButton"
      variant="secondary"
      size="sm"
      leftIcon="plus"
      :disabled="isLimitReached"
      @click="emit('add')"
    >
      {{ t('ui.components.passengersCard.add') }}
    </ActionButton>

    <div v-if="hasPassengers" class="PassengersCard-limit" :class="{ 'is-max': isLimitReached }">
      <I18nT tag="p" class="PassengersCard-limitLabel" keypath="ui.components.passengersCard.progress">
        <template #planName>
          {{ t(`custom.plans.${planCode}.title`) }}
        </template>
        <template #slots>
          <span class="PassengersCard-limitSlots">{{ t('ui.components.passengersCard.progressSlots', { used: passengers.length, max: maxPassengers }, passengers.length) }}</span>
        </template>
      </I18nT>

      <ProgressBar
        class="PassengersCard-progressBar"
        :progress="usedBeneficiariesProgress"
      />
    </div>

    <PassengersAccordion
      v-if="hasPassengers"
      :passengers="accordionPassengers"
      class="PassengersCard-list"
    >
      <template #content="{ passenger }">
        <PassengersCardContent
          :passenger="passengerById(passenger.id)"
          @edit="emit('edit', $event)"
          @remove="emit('remove', $event)"
        />
      </template>
    </PassengersAccordion>

    <p v-if="isLimitReached" class="PassengersCard-disclaimer">
      {{ t('ui.components.passengersCard.disclaimer') }}
    </p>
  </Card>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from '@cvo/i18n/composables/useI18n';
import Card from '@cvo/ui/components/atoms/Card/Card.vue';
import ActionButton from '@cvo/ui/components/atoms/ActionButton/ActionButton.vue';
import ProgressBar from '@cvo/ui/components/atoms/ProgressBar/ProgressBar.vue';
import PassengersAccordion from '@cvo/ui/components/molecules/PassengersAccordion/PassengersAccordion.vue';
import type { PassengersAccordionPassenger } from '@cvo/ui/components/molecules/PassengersAccordion/PassengersAccordion.model';
import { getPersonFullName } from '@cvo/domain';
import PassengersCardContent from './PassengersCardContent.vue';
import type { PassengersCardProps } from './PassengersCard.model';

const props = defineProps<PassengersCardProps>();
const emit = defineEmits<{
  add: [];
  edit: [passengerId: string];
  remove: [passengerId: string];
}>();

const { t } = useI18n();

const isLimitReached = computed(() => props.passengers.length >= props.maxPassengers);

const usedBeneficiariesProgress = computed(() => {
  if (props.maxPassengers <= 0) {
    return 0;
  }

  return Math.min((props.passengers.length / props.maxPassengers) * 100, 100);
});

const hasPassengers = computed(() => props.passengers.length >= 1);

const accordionPassengers = computed<PassengersAccordionPassenger[]>(() => props.passengers.map((passenger) => ({
  id: passenger.id,
  name: getPersonFullName(passenger),
  tag: passenger.relationship,
})));

function passengerById(id: string) {
  return props.passengers.find((passenger) => passenger.id === id)!;
}
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/responsive';
@use '@cvo/ui/styles/utils/text';

@layer cvo-ui {
  .PassengersCard {
    &.Card {
      background-color: vars.$organisms-card-passengers-bg-color;
      border: vars.$organisms-card-passengers-border-stroke solid vars.$organisms-card-passengers-border-color;
      border-radius: vars.$organisms-card-passengers-lg-md-border-radius;
      padding-block: vars.$organisms-card-passengers-lg-md-padding-top-bottom;
      padding-inline: vars.$organisms-card-passengers-lg-md-padding-left-right;

      &-title {
        color: vars.$organisms-card-passengers-title-color;
      }

      @include responsive.mobile {
        border-radius: vars.$organisms-card-passengers-sm-border-radius;
        padding-block: vars.$organisms-card-passengers-sm-padding-top-bottom;
        padding-inline: vars.$organisms-card-passengers-sm-padding-left-right;
      }
    }

    &-header {
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-2;
    }

    &-description {
      color: vars.$organisms-card-passengers-text-color;
      margin: 0;
      @include text.text-responsive('sm', 'md');
    }

    &-limit {
      .PassengersCard-limitLabel {
        color: vars.$organisms-card-passengers-text-color;
        margin-bottom: vars.$dimensions-d-2;
        @include text.text('xs');
      }

      .PassengersCard-progressBar {
        margin-bottom: vars.$dimensions-d-6;
        --progress-bg-color: #{vars.$organisms-card-passengers-quota-bar-bg-color};
        --progress-fill-color: #{vars.$organisms-card-passengers-quota-bar-color};
      }

      &.is-max {
        .PassengersCard-progressBar {
          --progress-bg-color: #{vars.$color-system-status-success-500};
          --progress-fill-color: #{vars.$organisms-card-passengers-quota-bar-color-full};
        }
        .PassengersCard-limitSlots {
          color: vars.$organisms-card-passengers-quota-text-color-full;
          font-weight: vars.$typography-weight-strong;
        }
      }
    }

    &-addButton {
      align-self: flex-start;
      margin-bottom: vars.$dimensions-d-6;

      @include responsive.mobile {
        align-self: auto;
        inline-size: 100%;
      }
    }

    &-list {
      display: flex;
      flex-direction: column;
    }

    &-disclaimer {
      color: vars.$organisms-card-passengers-disclaimer-color;
      margin: 0;
      @include text.text('xs');
    }
  }
}
</style>
